<template>
  <b-container>
    <b-button v-show="aspect.aspect_form_id == 'c1af6c58-38a6-11e4-90ff-5f95cb3dcd4d'" @click="fetchClientCode"
      variant="warning" class="float-right">Client Code</b-button>
<b-button
  v-show="aspect.aspect_form_id === 'c1af6c58-38a6-11e4-90ff-5f95cb3dcd4d'"
  variant="success"
  class="float-right mx-2"
  @click="handleSendReview"
  :disabled="reviewEmailStatus === 'sending'"
>
  <template v-if="reviewEmailStatus === 'sent'">
    <font-awesome-icon icon="check-circle" class="mr-1" />
    Review Email Sent
  </template>
  <template v-else-if="reviewEmailStatus === 'sending'">
    <b-spinner small class="mr-1" />
    Sending...
  </template>
  <template v-else>
    Send Review Email
  </template>
</b-button>
    <b-button :href="`/aspect/${aspect.id}/edit`" class="float-right">Edit</b-button>
    <h1>Aspect {{ aspect.name }}</h1>
    <b-alert variant="info" show>
      <span class="float-right">Updated on {{ aspect.date_updated | fullDate }}</span>
      Status: New
    </b-alert>
    <!-- Dates -->
    <b-list-group flush class="border-bottom border-1" v-if="form && form.json && form.json.settings">
      <b-list-group-item v-if="aspect.date_new">
        <strong>{{ form.json.settings.date_new }} </strong>
        <span :id="`date_new_copy`">{{ aspect.date_new  | onlyDate }}</span>
        <span v-if="form.json.settings.date_new == 'Date of Birth'" class="mx-1">
          (age: {{ aspect.date_new | age }})</span>
        <b-icon icon="clipboard"
          @click="copyToClipboard(`date_new_copy`)" />
      </b-list-group-item>
      <b-list-group-item v-if="aspect.date_purchased">
        <strong>{{ form.json.settings.date_purchased }} </strong>
        <span :id="`date_purchased_copy`">{{ aspect.date_purchased  | onlyDate }}</span> <b-icon icon="clipboard"
          @click="copyToClipboard(`date_purchased_copy`)" />
      </b-list-group-item>
    </b-list-group>
    <!-- Attributes -->
    <b-list-group flush v-if="attributes_show">
      <b-list-group-item v-for="(attribute, i) in attributes" :key="attribute.name">
        <strong>{{ getAttributeLabel(attribute.name) }} </strong>
        <span :id="`${i}_copy`">{{ attribute.data }}</span> <b-icon icon="clipboard"
          @click="copyToClipboard(`${i}_copy`)" />
      </b-list-group-item>
    </b-list-group>

    <!-- Tabs -->
    <b-tabs content-class="mt-3">
      <b-tab title="Notes">
        <NotesList :list="notes" :ref_id="$route.params.aspect_key" />
      </b-tab>
      <b-tab title="Tasks">
        <TasksList :ref_id="$route.params.aspect_key" parent_type="aspect" />
      </b-tab>
      <b-tab title="Policies">
        <PoliciesList :list="policies" :search_term="$route.params.aspect_key" :aspect_id="$route.params.aspect_key" />
      </b-tab>
      <b-tab title="Aspects" v-if="aspect.aspect_form_id">
        <AspectsList :list="aspects" :ref_id="this.$route.params.aspect_key" :ref_type="`aspect`" :form_type="aspect.aspect_form_id" />         
      </b-tab>
      <b-tab title="Attachments">
        <AttachmentsList :list="attachments" :ref_id="$route.params.aspect_key" ref_type="aspect" @update="attached" />
      </b-tab>
    </b-tabs>
    <b-modal id="client-code-modal" v-model="showClientCodeModal" title="Client Code Info" size="lg" hide-footer>
      <div v-if="clientCodeData && clientCodeData.code">
        <p>
          To login with this user's information, click the link below and then enter this one-use code into the form.
        </p>
        <p>
          Visit:
          <a :href="`https://clients.hayekinsurance.com/code?email=${aspect.attribute9}`" id="portal_link"
            target="_blank">
            Client Portal
          </a>
        </p>
        <h2 style="border:0">
          Code:
          <span id="portal_code">{{ clientCodeData.code }}</span>
        </h2>
      </div>
      <div v-else class="text-muted text-center">
        Loading client code details...
      </div>
    </b-modal>
  </b-container>

</template>

<script>
import AspectDataService from "@/services/AspectDataService";
import FormDataService from "@/services/FormDataService";
import AttachmentDataService from "@/services/AttachmentDataService";
import ReferenceDataService from "@/services/ReferenceDataService";
export default {
  props: ["company"],
  components: {
    AspectsList: () => import("./AspectsList.vue"),
    NotesList: () => import("../Note/NotesList.vue"),
    PoliciesList: () => import("../Policy/PoliciesList.vue"),
    AttachmentsList: () => import("../Attachment/AttachmentsList.vue"),
    TasksList: () => import("../Task/TasksList.vue"),
  },
  data() {
    return {
      aspect: {},
      aspects: [],
      attributes: [],
      attachments: [],
      policies: [],
      notes: [],
      form: null,
      attached_data: false,
      attributes_show: false,
      loading: false, // Loading state for better UX
      status_codes: [],
      showClientCodeModal: false,
      clientCodeData: null,
      reviewEmailStatus: 'idle',
    };
  },
  async created() {

    this.status_codes = ReferenceDataService.status_codes()

    this.loading = true;
    try {
      const aspectKey = this.$route.params.aspect_key;

      // Fetch all data concurrently
      const [aspectResponse, attachmentsResponse, policiesResponse] = await Promise.all([
        AspectDataService.getById(aspectKey),
        AttachmentDataService.getByRef(aspectKey),
        AspectDataService.getPolicies(aspectKey),
      ]);

      // Validate and assign aspect data
      if (aspectResponse?.data) {
        this.aspect = aspectResponse.data;
      }

      // Validate and assign attachments data
      if (Array.isArray(attachmentsResponse?.data)) {
        this.attachments = attachmentsResponse.data;
      } else {
        console.warn("Attachments response is not an array:", attachmentsResponse.data);
      }

      // Validate and assign policies data
      if (Array.isArray(policiesResponse?.data)) {
        this.policies = policiesResponse.data;
      } else {
        console.warn("Policies response is not an array:", policiesResponse.data);
      }

      // Fetch form data if aspect_form_id exists
      if (this.aspect.aspect_form_id) {
        const formResponse = await FormDataService.get(this.aspect.aspect_form_id);
        if (formResponse?.data) {
          this.form = formResponse.data;
        }
      }

      // Process attributes if present
      if (Array.isArray(this.aspect.attributes)) {
        this.attributes = this.aspect.attributes.filter((attribute) => attribute?.data?.length > 0);
        this.attributes_show = this.attributes.length > 0;
      }
    } catch (error) {
      console.error("Error loading aspect data:", error);
      this.$emit("error", { message: "Failed to load aspect data. Please try again.", error });
    } finally {
      this.loading = false;
    }

  },
  watch: {
    attached_data(newValue) {
      if (newValue) {
        this.refreshAttachments();
        this.attached_data = false;
      }
    },
  },
  methods: {
    getAttributeLabel(attributeName) {
      // attributeName is like "attribute1", "attribute2", etc.
      // Extract the column number and find the matching attribute in form.json.attributes
      if (!this.form?.json?.attributes) return "Unknown Attribute";

      const columnMatch = attributeName.match(/attribute(\d+)/);
      if (!columnMatch) return "Unknown Attribute";

      const columnStr = columnMatch[1]; // Keep as string since form.json.attributes uses string columns
      const attr = this.form.json.attributes.find(a => String(a.column) === columnStr);
      return attr?.label || "Unknown Attribute";
    },
  async handleSendReview() {
    this.reviewEmailStatus = 'sending';
    try {
      await AspectDataService.sendReview(this.aspect.id);
      this.reviewEmailStatus = 'sent';
    } catch (err) {
      console.error('Failed to send review email', err);
      this.reviewEmailStatus = 'error';
    }
  },    
    async fetchClientCode() {
      try {
        const res = await AspectDataService.getCode(this.$route.params.aspect_key); // Adjust URL as needed
        this.clientCodeData = res.data;
        this.showClientCodeModal = true;
      } catch (err) {
        console.error('Failed to fetch client code:', err);
        this.$emit('error', err);
      }
    },
    attached() {
      this.attached_data = true;
    },
    async refreshAttachments() {
      try {
        const attachmentsResponse = await AttachmentDataService.getByRef(this.$route.params.aspect_key);
        if (Array.isArray(attachmentsResponse?.data)) {
          this.attachments = attachmentsResponse.data;
          this.$emit("attachments-updated", this.attachments);
        } else {
          console.warn("Refresh attachments response is not an array:", attachmentsResponse.data);
        }
      } catch (error) {
        console.error("Error refreshing attachments:", error);
        this.$emit("data-error", { message: "Failed to refresh attachments.", error });
      }
    },
    async copyToClipboard(elementId) {
      try {
        const element = document.getElementById(elementId);

        if (!element) {
          alert("Element not found!");
          return;
        }

        const text = element.textContent || element.innerText;
        await navigator.clipboard.writeText(text);

        alert(`Copied: "${text}"`);
      } catch (error) {
        console.error("Error copying to clipboard:", error);
        alert("Failed to copy content.");
      }
    },
  },
};
</script>
